import type {BigBagResumen, CampoDeBigBag, FiltrosDeInventario} from '@cnc/contracts';
import {tr, txt, trx} from '../i18n/idioma';

// Campos editables de un Big Bag: los usan la ficha (formulario), la edicion
// rapida de la tabla y el cambio en lote, para que los tres se comporten igual.
export type TipoDeCampo = 'texto' | 'numero' | 'fecha' | 'mes' | 'lista';
export type ListaDeOpciones = 'minas' | 'proveedores' | 'exportadores' | 'plantas' | 'ubicaciones' | 'recolecciones';
export type CampoBigBag = {
  campo: CampoDeBigBag;
  etiqueta: ReturnType<typeof txt>;
  tipo: TipoDeCampo;
  lista?: ListaDeOpciones;
};

export const GRUPOS_DE_CAMPOS: Array<{titulo: ReturnType<typeof txt>; campos: CampoBigBag[]}> = [
  {
    titulo: txt('Identificación', 'Identification'),
    campos: [
      {campo: 'recoleccion', etiqueta: txt('Recolección', 'Pickup'), tipo: 'lista', lista: 'recolecciones'},
      {campo: 'contenedor', etiqueta: txt('Contenedor', 'Container'), tipo: 'texto'},
      {campo: 'sello', etiqueta: txt('Sello', 'Seal'), tipo: 'texto'},
      {campo: 'tipoMaterial', etiqueta: txt('Tipo de material', 'Material type'), tipo: 'texto'},
    ],
  },
  {
    titulo: txt('Pesos y tenores', 'Weights and grades'),
    campos: [
      {campo: 'tonHumedas', etiqueta: txt('Toneladas húmedas', 'Wet tons'), tipo: 'numero'},
      {campo: 'tonSecas', etiqueta: txt('Toneladas secas', 'Dry tons'), tipo: 'numero'},
      {campo: 'auGt', etiqueta: txt('Au (g/t)', 'Au (g/t)'), tipo: 'numero'},
      {campo: 'auG', etiqueta: txt('Au (g)', 'Au (g)'), tipo: 'numero'},
      {campo: 'agGt', etiqueta: txt('Ag (g/t)', 'Ag (g/t)'), tipo: 'numero'},
      {campo: 'agG', etiqueta: txt('Ag (g)', 'Ag (g)'), tipo: 'numero'},
    ],
  },
  {
    titulo: txt('Origen', 'Origin'),
    campos: [
      {campo: 'mina', etiqueta: txt('Mina', 'Mine'), tipo: 'lista', lista: 'minas'},
      {campo: 'proveedor', etiqueta: txt('Proveedor', 'Supplier'), tipo: 'lista', lista: 'proveedores'},
      {campo: 'exportador', etiqueta: txt('Exportador', 'Exporter'), tipo: 'lista', lista: 'exportadores'},
      {campo: 'planta', etiqueta: txt('Planta', 'Plant'), tipo: 'lista', lista: 'plantas'},
      {campo: 'ubicacion', etiqueta: txt('Bodega', 'Warehouse'), tipo: 'lista', lista: 'ubicaciones'},
    ],
  },
  {
    titulo: txt('Fechas', 'Dates'),
    campos: [
      {campo: 'fechaEmpaque', etiqueta: txt('Fecha de empaque', 'Packing date'), tipo: 'fecha'},
      {campo: 'mesMolienda', etiqueta: txt('Mes de molienda', 'Grinding month'), tipo: 'mes'},
      {campo: 'entradaBodega', etiqueta: txt('Entrada a bodega', 'Warehouse entry'), tipo: 'fecha'},
      {campo: 'fechaLlenado', etiqueta: txt('Día de llenado', 'Filling date'), tipo: 'fecha'},
    ],
  },
];

export const CAMPOS_BIG_BAG = GRUPOS_DE_CAMPOS.flatMap(g => g.campos);
export const CAMPO_BIG_BAG = new Map(CAMPOS_BIG_BAG.map(c => [c.campo as string, c]));

// Valor del campo en el Big Bag, como texto para un formulario.
export function valorInicial(bolsa: BigBagResumen, campo: CampoDeBigBag): string {
  if (campo === 'mesMolienda') return bolsa.mesMolienda?.slice(0, 7) ?? '';
  const valor = bolsa[campo];
  return valor === null || valor === undefined ? '' : String(valor);
}

// Texto escrito → valor para la API (los numeros aceptan coma decimal; vacio borra el dato).
export function convertirValor(c: CampoBigBag, texto: string): {valor: string | number | null} | {error: string} {
  const limpio = texto.trim();
  if (c.tipo !== 'numero') return {valor: limpio || null};
  const numero = Number(limpio.replace(',', '.'));
  if (limpio === '' || !Number.isFinite(numero))
    return {error: tr(`${trx(c.etiqueta)}: escribe un número.`, `${trx(c.etiqueta)}: enter a number.`)};
  return {valor: numero};
}

// Opciones de las listas (minas, proveedores...) a partir de los filtros del inventario.
export function opcionesDeListas(opciones: FiltrosDeInventario | undefined): Record<ListaDeOpciones, string[]> {
  const listas: Record<ListaDeOpciones, string[]> = {
    minas: [],
    proveedores: [],
    exportadores: [],
    plantas: [],
    ubicaciones: [],
    recolecciones: [],
  };
  if (opciones)
    for (const clave of Object.keys(listas) as ListaDeOpciones[])
      listas[clave] = opciones[clave].map(o => o.valor).filter(v => v && v !== 'sin');
  return listas;
}

export const tipoDeEntrada = (tipo: TipoDeCampo) => (tipo === 'fecha' ? 'date' : tipo === 'mes' ? 'month' : 'text');
